import { Deployment } from '@/types/deployment';
import { isServer } from '@tanstack/react-query';
import { logger } from '@/utils/logger';
import { Services } from '@/data/enums/services';

type baseUrlProps = {
    apiService: Services;
    backend?: boolean;
    deployment: Deployment;
    path: string;
};

const log = logger('deploymentUrl');

/** The authority placeholder in every host template — see data/helpers/buildUrl.ts. */
const PUBLIC_AUTHORITY = 'https://DEPLOYMENT.proxibid.com';

/**
 * Builds a request URL for a given environment identity.
 *
 * `deployment` is always a **public subdomain token** (see utils/getDeployment.ts). Internal-vs-public
 * routing is decided here, not by the identity value:
 *
 * - **Server-side with SSR_URL set** — swap the whole public authority for the internal k8s service so SSR
 *   traffic stays inside the cluster and never traverses the external load balancer (WBPR-3680).
 * - **Everything else** — substitute the public subdomain.
 */
export const getDeploymentUrl = ({ apiService, backend = false, deployment, path = '' }: baseUrlProps) => {
    let url: string = '';
    const ssrUrl = process.env.SSR_URL;

    // `isServer` is load-bearing: without it every browser fetch would be rewritten to an internal host that
    // is unreachable from outside the cluster. deploymentUrl.spec.ts pins the client side of this.
    if (isServer && ssrUrl !== undefined && ssrUrl !== '') {
        // SSR_URL may arrive as 'wavebid-a2o-service', 'http://wavebid-a2o-service', or include a port
        // (dockerized e2e uses 'wavebid-wavebid-a2o-service-1:8080').
        const internalHost = ssrUrl.replace(/^https?:\/\//, '');
        // noinspection HttpUrlsUsage
        url = path.replace(PUBLIC_AUTHORITY, `http://${internalHost}`);
    } else if (deployment === Deployment.Local) {
        // Our api calls hit port 8080, but the calls to get the images stored on s3 hit 7080.
        // MUST stay inside this chain, after the isServer branch: it is now reachable server-side (dockerized
        // e2e sets NEXT_PUBLIC_DEPLOYMENT=local *and* SSR_URL), and as a trailing unconditional reassignment
        // it would rewrite in-cluster SSR fetches to localhost, which fails silently via prefetchQuery.
        url = path.replace(PUBLIC_AUTHORITY, `http://localhost:${backend ? '7' : '8'}080`);
    } else if (deployment.includes('-pr') && apiService === Services.Classic) {
        // Classic is not deployed per-PR, so preview browsers fall back to shared dev.
        url = path.replace('DEPLOYMENT', Deployment.Dev);
    } else {
        url = path.replace('DEPLOYMENT', deployment);
    }

    log.info(`deploymentUrl: ${url}`);
    return url;
};
